import { useEffect, useState } from 'react';
import useApi from '@/hooks/useApi';

export enum Certainty {
  HIGH = 'high',
  LOW = 'low',
}

type CountryResult = {
  language: string;
  country: string | undefined;
  certainty: Certainty;
};

const getLocaleCountry = (): CountryResult => {
  const locale = new Intl.Locale(navigator.language);

  if (locale.region) {
    return {
      language: locale.language.toUpperCase(),
      country: locale.region.toUpperCase(),
      certainty: Certainty.HIGH,
    };
  }

  const maximized = locale.maximize();
  return {
    language: locale.language.toUpperCase(),
    country: maximized.region?.toUpperCase(),
    certainty: Certainty.LOW,
  };
};

const CACHE_KEY = 'geo_country_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachedCountry = {
  country: string;
  language: string;
  timestamp: number;
};

const getCachedResult = (): CountryResult | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const cached = JSON.parse(raw) as CachedCountry;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return {
      language: cached.language,
      country: cached.country,
      certainty: Certainty.HIGH,
    };
  } catch {
    return null;
  }
};

const setCachedResult = (language: string, country: string): void => {
  try {
    const entry: CachedCountry = { country, language, timestamp: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // storage not available
  }
};

const useCountryWithCertainty = (): CountryResult => {
  const [fetchCountry] = useApi('geo.country');
  const [result, setResult] = useState<CountryResult>(() => {
    return getCachedResult() ?? getLocaleCountry();
  });

  useEffect(() => {
    if (getCachedResult()) {
      return;
    }

    let cancelled = false;

    const detectCountry = async () => {
      const res = await fetchCountry();
      const code = res?.response?.country;
      if (cancelled || !code) {
        return;
      }
      const locale = new Intl.Locale(navigator.language);
      const language = locale.language.toUpperCase();
      const country = code.toUpperCase();
      setCachedResult(language, country);
      setResult({ language, country, certainty: Certainty.HIGH });
    };

    detectCountry().catch(() => {
      // fallback already set as initial state
    });

    return () => {
      cancelled = true;
    };
  }, [fetchCountry]);

  return result;
};

export default useCountryWithCertainty;
